import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, IconButton, TextField, Typography, Stack,
  CircularProgress, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

// Bu akisin etiketlerini yonet: ekle / adi & rengi duzenle / sil.
// Etiket bu akista tanimlidir; baska akislarda gorunmez.
const TAG_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#22c55e', '#10b981', '#06b6d4',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#ec4899', '#f43f5e', '#71717a', '#0f172a',
];

const ColorSwatch = ({ color, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      width: 22, height: 22, borderRadius: '6px', bgcolor: color,
      cursor: 'pointer', border: '2px solid',
      borderColor: selected ? '#0f172a' : 'transparent',
      transition: 'transform 0.12s',
      '&:hover': { transform: 'scale(1.1)' },
    }}
  />
);

const FlowTagManagerDialog = ({
  open, onClose, tags, onCreate, onUpdate, onDelete,
}) => {
  const { t } = useTranslation();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(TAG_COLORS[8]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState(TAG_COLORS[8]);
  const [deletingId, setDeletingId] = useState(null);

  const handleCreate = async () => {
    if (!newName.trim() || saving) return;
    setSaving(true);
    try {
      await onCreate({ name: newName.trim(), color: newColor });
      setNewName('');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (tag) => {
    setEditingId(tag.id); setEditName(tag.name); setEditColor(tag.color);
  };

  const cancelEdit = () => {
    setEditingId(null); setEditName(''); setEditColor(TAG_COLORS[8]);
  };

  const handleUpdate = async (tag) => {
    if (!editName.trim()) return;
    try {
      await onUpdate({ id: tag.id, name: editName.trim(), color: editColor });
      cancelEdit();
    } catch (_e) { /* handled upstream */ }
  };

  const handleDelete = async (id) => {
    if (deletingId != null) return;
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, overflow: 'hidden' } } }}
    >
      <DialogTitle sx={{ pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>
          {t('reportManagement.tagManager.title', { defaultValue: 'Etiketleri Yönet' })}
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Yeni etiket ekleme formu */}
        <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
            {t('reportManagement.tagManager.newTag', { defaultValue: 'Yeni Etiket' })}
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              size="small" fullWidth autoFocus
              value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder={t('reportManagement.tagManager.namePlaceholder', { defaultValue: 'Etiket adı' })}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {TAG_COLORS.map((c) => (
                <ColorSwatch key={c} color={c} selected={c === newColor} onClick={() => setNewColor(c)} />
              ))}
            </Box>
            <Box>
              <Button
                variant="contained" size="small"
                onClick={handleCreate}
                disabled={!newName.trim() || saving}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {t('reportManagement.tagManager.add', { defaultValue: 'Ekle' })}
              </Button>
            </Box>
          </Stack>
        </Box>

        {/* Mevcut etiketler */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
          {t('reportManagement.tagManager.existing', { defaultValue: 'Mevcut Etiketler' })} ({tags.length})
        </Typography>
        {tags.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            {t('reportManagement.tagManager.empty', { defaultValue: 'Henüz etiket yok. Yukarıdan ilk etiketi ekleyin.' })}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {tags.map((tag) => {
              const isEditing = editingId === tag.id;
              if (isEditing) {
                return (
                  <Box
                    key={tag.id}
                    sx={{
                      p: 1.5, borderRadius: 2,
                      border: '1px solid #cbd5e1', bgcolor: '#f8fafc',
                    }}
                  >
                    <Stack spacing={1}>
                      <TextField
                        size="small" autoFocus fullWidth
                        value={editName} onChange={(e) => setEditName(e.target.value)}
                      />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {TAG_COLORS.map((c) => (
                          <ColorSwatch key={c} color={c} selected={c === editColor} onClick={() => setEditColor(c)} />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                        <Button
                          size="small" onClick={cancelEdit}
                          sx={{ textTransform: 'none', minWidth: 0 }}
                        >
                          {t('common.cancel', { defaultValue: 'İptal' })}
                        </Button>
                        <Button
                          size="small" variant="contained"
                          onClick={() => handleUpdate(tag)} disabled={!editName.trim()}
                          startIcon={<CheckIcon />}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          {t('common.save', { defaultValue: 'Kaydet' })}
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                );
              }
              return (
                <Box
                  key={tag.id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    p: 1, borderRadius: 2, border: '1px solid #e2e8f0',
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}
                >
                  <Chip
                    onClick={() => {}}
                    label={tag.name}
                    size="small"
                    sx={{
                      bgcolor: tag.color, color: '#fff',
                      fontWeight: 600, height: 22,
                      flex: 1, justifyContent: 'flex-start',
                    }}
                  />
                  <IconButton size="small" onClick={() => startEdit(tag)}>
                    <EditOutlinedIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
                  </IconButton>
                  <IconButton
                    size="small" onClick={() => handleDelete(tag.id)}
                    disabled={deletingId === tag.id}
                  >
                    {deletingId === tag.id
                      ? <CircularProgress size={14} />
                      : <DeleteOutlineIcon sx={{ fontSize: '1rem', color: '#ef4444' }} />}
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('common.close', { defaultValue: 'Kapat' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowTagManagerDialog;
